import useCopilotPatientsList from 'components/CopilotControl/hooks/useCopilotPatientsList'
import _ from 'lodash'
import { TxTrackingListTypes } from 'consts/txTrackingReportConsts'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useFetchTxTrackingPatientsInfiniteQuery } from 'store/modules/TxTrackingReport'
import PatientsListError from '../../components/CopilotPatientsLists/PatientsListError'
import PatientsSentimentHeaderSkeleton from '../../components/CopilotPatientsLists/PatientsSentimentList/PatientsSentimentHeaderSkeleton'
import PatientsList from '../../components/common/PatientsList/PatientsList'
import OralHygieneEmptyState from '../../OralHygieneReport/patientsList/OralHygieneEmptyState'
import { getTxTrackingCategories, getTxTrackingViewTotal } from '../txTrackingReportLogic'
import TxTrackingListHeader from './TxTrackingListHeader'
import TxTrackingPatientListItem from './TxTrackingPatientListItem'

const TxTrackingPatientsList = ({
  accountOwnerId,
  listType,
  onListTypeSelected,
  selectedCategories,
  setSelectedCategories,
  pastXDays,
  aggregationData,
  isAggregationLoading
}) => {
  const { isStatusesLoading } = useCopilotPatientsList()

  const {
    data: patients = [],
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    fetchNextPage
  } = useFetchTxTrackingPatientsInfiniteQuery(
    {
      accountOwnerId,
      listType,
      categories: selectedCategories,
      pastXDays
    },
    {
      skip: !accountOwnerId
    }
  )

  // Chip counts are the complete category totals of the selected period; row filtering never changes them.
  const categories = useMemo(() => getTxTrackingCategories(aggregationData, listType), [aggregationData, listType])
  const viewTotal = useMemo(() => getTxTrackingViewTotal(aggregationData, listType), [aggregationData, listType])

  // Reasons get a chip only when tagged in the period. A selected reason missing from a new period
  // would keep filtering the list with no chip left to clear it, so it is dropped.
  useEffect(() => {
    if (listType !== TxTrackingListTypes.TrackingReasons || isAggregationLoading) {
      return
    }

    const taggedSelectedCategories = selectedCategories.filter(selectedCategory =>
      categories.some(category => category.key === selectedCategory)
    )
    if (taggedSelectedCategories.length !== selectedCategories.length) {
      setSelectedCategories(taggedSelectedCategories)
    }
  }, [listType, isAggregationLoading, categories, selectedCategories, setSelectedCategories])

  const patientsPaginatedData = useMemo(() => {
    return patients.pages?.map(page => page.items).flat() || []
  }, [patients])

  const hasMorePages = _.last(patients?.pages || [])?.hasMore
  const isListLoading = isLoading || isStatusesLoading || isFetching

  const PatientListItemComponent = useCallback(
    props => <TxTrackingPatientListItem {...props} listType={listType} pastXDays={pastXDays} />,
    [listType, pastXDays]
  )

  return (
    <PatientsList
      isHeaderLoading={isAggregationLoading}
      isListLoading={isListLoading && !isFetchingNextPage}
      isEmpty={false}
      fetchNextPage={fetchNextPage}
      hasMorePages={hasMorePages}
      patients={patientsPaginatedData}
      EmptyStateComponent={isError ? <PatientsListError /> : <OralHygieneEmptyState />}
      HeaderSkeletonComponent={<PatientsSentimentHeaderSkeleton />}
      PatientsHeaderComponent={
        <TxTrackingListHeader
          listType={listType}
          onListTypeSelected={onListTypeSelected}
          categories={categories}
          viewTotal={viewTotal}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          pastXDays={pastXDays}
        />
      }
      PatientListItemComponent={PatientListItemComponent}
    />
  )
}

export default TxTrackingPatientsList
